// #38 - Principal downline view. Renders the principal's downline as a nested
// AGENCY tree with agents at every level: their agency -> sub-agencies -> ... with
// each agency's agents as leaves. The whole tree arrives in one payload from
// /api/agency/downline-tree, so expand/collapse is pure UI state. Collapsed to the
// top tiers by default; each agency node also has a collapsible "Agents (N)" group
// so a top-heavy agency doesn't wall the page. Clicking an agent opens AgentEdit
// (a principal may edit an agent's licensed states).
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import PersonIcon from "@mui/icons-material/Person";
import StartRequestButtons from "../Agent/StartRequestButtons";
import { Title } from "react-admin";
import AgencyService from "../../../services/agency.service";
import { filterTree, parseTerms } from "./filterTree";

const rowSx = (depth) => ({
  display: "flex",
  alignItems: "center",
  gap: 1,
  py: 0.5,
  pl: depth * 3,
  cursor: "pointer",
  borderRadius: 1,
  "&:hover": { bgcolor: "action.hover" },
});

const Caret = ({ open }) =>
  open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />;

const AgentLeaf = ({ agent, depth }) => {
  const navigate = useNavigate();
  return (
    <Box sx={rowSx(depth)} onClick={() => navigate(`/admin/agent/${agent.id}`)}>
      <PersonIcon fontSize="small" sx={{ color: "text.secondary" }} />
      <Typography variant="body2">
        {agent.firstName} {agent.lastName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        NPN {agent.npn || "—"}
      </Typography>
      {agent.status ? (
        <Chip size="small" variant="outlined" label={agent.status} />
      ) : null}
      <Typography variant="caption" color="text.secondary">
        {agent.email}
      </Typography>
      {/* #57 - start a request on this agent's behalf, right from the tree. */}
      <Box sx={{ marginLeft: "auto" }}>
        <StartRequestButtons record={agent} />
      </Box>
    </Box>
  );
};

const AgentGroup = ({ agents, depth, forceOpen }) => {
  const [open, setOpen] = useState(!!forceOpen);
  return (
    <>
      <Box sx={rowSx(depth)} onClick={() => setOpen((o) => !o)}>
        <Caret open={open} />
        <GroupIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography variant="body2" color="text.secondary">
          Agents ({agents.length})
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        {agents.map((a) => (
          <AgentLeaf key={a.id} agent={a} depth={depth + 1} />
        ))}
      </Collapse>
    </>
  );
};

const AgencyNode = ({ node, depth, defaultOpen, forceOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen || !!forceOpen);
  const hasContent = node.children.length > 0 || node.agents.length > 0;
  return (
    <>
      <Box sx={rowSx(depth)} onClick={() => hasContent && setOpen((o) => !o)}>
        {hasContent ? <Caret open={open} /> : <Box sx={{ width: 20 }} />}
        <BusinessIcon fontSize="small" color="primary" />
        <Typography fontWeight={600}>{node.name}</Typography>
        <Chip
          size="small"
          color="primary"
          label={`${node.totalAgents} agent${node.totalAgents === 1 ? "" : "s"}`}
        />
        {node.totalAgencies > 0 ? (
          <Chip
            size="small"
            variant="outlined"
            label={`${node.totalAgencies} sub-${node.totalAgencies === 1 ? "agency" : "agencies"}`}
          />
        ) : null}
      </Box>
      <Collapse in={open} unmountOnExit>
        {node.children.map((c) => (
          <AgencyNode
            key={c.id}
            node={c}
            depth={depth + 1}
            defaultOpen={false}
            forceOpen={forceOpen}
          />
        ))}
        {node.agents.length > 0 ? (
          <AgentGroup agents={node.agents} depth={depth + 1} forceOpen={forceOpen} />
        ) : null}
      </Collapse>
    </>
  );
};

const MyDownline = () => {
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    AgencyService.downlineTree()
      .then(setTree)
      .catch(() => setError("We couldn't load your downline."));
  }, []);

  const empty = tree && tree.totalAgents === 0 && tree.children.length === 0;

  // #84 - filter the tree client-side; while a filter is active every node is
  // expanded (forceOpen) so the matches are visible without clicking through.
  const terms = parseTerms(query);
  const shown = tree && terms.length ? filterTree(tree, terms) : tree;
  const noMatches = tree && !empty && terms.length > 0 && !shown;

  return (
    <Box sx={{ mt: 2, maxWidth: 1100 }}>
      <Title title="My Downline" />
      <Typography variant="h5" gutterBottom>
        My Downline
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Every agency and agent beneath you, by tier. Expand an agency to drill down;
        click an agent to view or edit their licensed states.
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!tree && !error ? <CircularProgress sx={{ mt: 3 }} /> : null}
      {empty ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No agencies or agents beneath your account yet.
        </Alert>
      ) : null}
      {tree && !empty ? (
        <TextField
          size="small"
          fullWidth
          sx={{ mt: 2 }}
          placeholder="Filter by agency, agent, NPN, or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "aria-label": "Filter downline" }}
        />
      ) : null}
      {noMatches ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Nothing in your downline matches "{query.trim()}".
        </Alert>
      ) : null}
      {shown && !empty ? (
        <Paper sx={{ p: 2, mt: 2 }} elevation={1}>
          <AgencyNode
            key={terms.join(" ") || "all"}
            node={shown}
            depth={0}
            defaultOpen
            forceOpen={terms.length > 0}
          />
        </Paper>
      ) : null}
    </Box>
  );
};

export default MyDownline;
